import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

import { PaymentProcessor } from '@r6digital/r6pay-types/types/PaymentProcessor';
import { NextAction } from '@r6digital/r6pay-types/types/Requests/PaymentLinkCreate';
import {
    creditCardType,
    validateCreditCardCSV,
    validateCreditCardNumber,
} from '@r6digital/rapidstor-types/react/entities/CreditCard';
import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { MoveInAutoBillPayload } from '@r6digital/rapidstor-types/react/entities/MoveIn';
import { AutoBillType } from '@r6digital/rapidstor-types/react/entities/Reservation';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import { RapidStorUnitAvailability } from '@r6digital/rapidstor-types/types/Api';
import {
    LineItem,
    RapidStorBoxShopCouponResponse,
} from '@r6digital/rapidstor-types/types/Merchandise';
import {
    BaseReservation,
    CostRetrieveResponse,
} from '@r6digital/rapidstor-types/types/Reservation';

import {
    useGetMerchandiseQuery,
    useGetMoveInCostRetrieveMutation,
    useGetMoveInWizardInitialisationQuery,
    useGetReservationQuery,
    useGetUnitAvailabilityMutation,
    usePatchReservationMutation,
    usePostMoveInMutation,
    usePostScaMoveInPrepareMutation,
} from '@/api/rapidstor';
import useCart from '@/hooks/useCart';
import { LoqateAddressFindResponseItem, LoqateValidationFields } from '@/types/Loqate';
import { WizardValidationError } from '@/types/Wizard';
import Logger from '@/utils/logging';
import { getDefaultNeededDate, RapidStorSettings } from '@/utils/rapidstor';
import { fadeOut } from '@/utils/ui';
import {
    excludedInputs,
    getAutobillFields,
    normaliseFieldName,
    validatePassword,
    validateString,
} from '@/utils/validation';

import store from '@/store';
import { setPersonaId } from '@/store/bookingSlice';

import Alert from '@/components/elements/alert';
import Button from '@/components/elements/button';
import CircleCheckbox from '@/components/elements/circle-checkbox';
import ErrorTag from '@/components/elements/error-tag';
import Spinner from '@/components/elements/spinner';
import WizardDescriptor from '@/components/rapidstor/wizard/descriptor';
import AccountDetails from '@/components/rapidstor/wizard/forms/account-details';
import AutoBill from '@/components/rapidstor/wizard/forms/autobill';
import ContactDetailsForm from '@/components/rapidstor/wizard/forms/contact-details';
import IdentityVerification from '@/components/rapidstor/wizard/forms/identity-verification';
import InsuranceCoverageComponent from '@/components/rapidstor/wizard/forms/insurance-coverage';
import Merchandise from '@/components/rapidstor/wizard/forms/merchandise';
import PaymentForm from '@/components/rapidstor/wizard/forms/payment-form';
import ProofOfAddress from '@/components/rapidstor/wizard/forms/proof-of-address';
import ProofOfInsurance from '@/components/rapidstor/wizard/forms/proof-of-insurance';
import ProductRemovalModal from '@/components/rapidstor/wizard/modals/product-removal';
import WizardPricingTable from '@/components/rapidstor/wizard/pricing-table';

import { deconstructScaErrorMessage } from '@/features/rapidstor/helpers';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { useR6PayContext } from '@/features/rapidstor/providers/R6PayServiceProvider';
import LoqateValidator from '@/features/rapidstor/services/LoqateService';
import ReservationService from '@/features/rapidstor/services/ReservationService';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import classes from '../scss/screens/move-in.module.scss';

import { CorporationCode, getCorpTranslations } from '../features/translations/helpers';
import { validateEmail } from '../utils/validation';
import { default as ErrorComponent } from './error';

interface LoqateAddressResults {
    main: LoqateAddressFindResponseItem[];
    alternate: LoqateAddressFindResponseItem[];
}

export default function MoveIn() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { token: reservationIdentifier } = useParams();
    const { t } = useTranslation();
    const r6PayService = useR6PayContext(location);
    const { unitList, selectionTypes, persona, setupSliceData, setUnavailableDescriptorIds, unavailableDescriptorIds } = useContext(JourneyContext);
    const { firebaseLogEvent } = useContext(AnalyticsContext);

    /** @TODO get rid of storing descriptor and unitType here. It's already in the bookingSlice */
    const [descriptor, setDescriptor] = useState<Descriptor>();
    const [unitType, setUnitType] = useState<UnitType>();
    const [reservation, setReservation] = useState<BaseReservation>();
    const [costRetrieve, setCostRetrieve] = useState<CostRetrieveResponse>();
    const [moveInError, setMoveInError] = useState<string>();
    const [moveInLoading, setMoveInLoading] = useState<boolean>(false);
    const [loqateLoading, setLoqateLoading] = useState<LoqateValidationFields>();
    const [loqateAddressResults, setLoqateAddressResults] =
        useState<LoqateAddressResults>();
    const [bankName, setBankName] = useState<string>();
    const [cookies, setCookie] = useCookies(['reservation', 'selectedCustomerType']);
    const [productRemoval, setProductRemoval] = useState<LineItem | null>(null);

    const [moveInCostRetrieve, { error: moveInCostRetrieveError }] = useGetMoveInCostRetrieveMutation();
    const [makeScaMoveInPrepare] = usePostScaMoveInPrepareMutation();
    const [makeMoveIn] = usePostMoveInMutation();
    const [getUnitAvailability] = useGetUnitAvailabilityMutation();
    const [patchReservation] = usePatchReservationMutation();

    const [searchParams] = useSearchParams();

    const { data: res, isSuccess: onSuccess } = useGetReservationQuery(
        {
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            token: reservationIdentifier,
        },
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const nowDate = useMemo(() => moment(res?.moveInContext?.neededDate || new Date().toISOString()), [res]);

    const {
        data: moveInWizardDataInit,
        isLoading: moveInInitLoading,
        isSuccess: moveInInitSuccess,
    } = useGetMoveInWizardInitialisationQuery(
        {
            corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
            locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
            reservationToken: res?.token,
        },
        {
            skip: !onSuccess,
        }
    );

    const handleUnitAvailability = useCallback(async () => {
        try {
            if(res) {
                const unitAvailabilityRes = await checkUnitAvailability(res._id);
                if (!unitAvailabilityRes.unit) {
                    fadeOut();
                    firebaseLogEvent('unit_unavailable_for_move_in', {});
                    setUnavailableDescriptorIds([...unavailableDescriptorIds, res.unitType.descriptor._id]);
                    setTimeout(() => {
                        navigate(
                            '/unit-listing',
                            {
                                state: {
                                    unitUnavailableAtMoveIn: true,
                                    unavailableDescriptor: res.unitType.descriptor._id
                                }
                            }
                        );
                    }, 300);
                    return false;
                }
                // @TODO: check if we need to update the local reservation with the new unit
                if (unitAvailabilityRes.updated) {
                    setReservation(unitAvailabilityRes.reservation);
                }
                return true;
            }
        } catch (error) {
            Logger.error(
                `[processBooking] Failed to process move-in request: ${JSON.stringify(error)}`,
                location,
                [
                    {
                        name: 'rapidstor_reservationToken',
                        value: `${res?.token}`,
                    },
                ]
            );
            setMoveInError(`Move-in failed: ${JSON.stringify(error)}`);
            setMoveInLoading(false);
            return false;
        }

        async function checkUnitAvailability (reservationId: string): Promise<RapidStorUnitAvailability> {
            const result = await getUnitAvailability({
                locationCode: location.locationCode,
                corpCode: location.corpCode,
                reservationId,
            }).unwrap();

            return result;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firebaseLogEvent, getUnitAvailability, location, navigate, res, unavailableDescriptorIds]);

    useEffect(() => {
        async function unitAvailabilityCheck() {
            if(res?._id && onSuccess) {
                await handleUnitAvailability();
            }
        }
        unitAvailabilityCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSuccess]);

    const { data: moveInWizardMerchandise, isSuccess: merchandiseSuccess } =
        useGetMerchandiseQuery({
            locationCode: location.locationCode,
            corpCode: location.corpCode
        }, {
            skip: !moveInInitSuccess,
        });

    const availableInsuranceCoverage = useMemo(() => {
        return moveInWizardDataInit?.siteInsuranceCoverage.filter((coverage) => {
            if (coverage.hidden || coverage.deleted) {
                return false;
            }

            if (coverage.maxArea && res?.unitType.area) {
                return parseInt(coverage.maxArea) >= res?.unitType.area;
            } else {
                return true;
            }
        });
    }, [moveInWizardDataInit?.siteInsuranceCoverage, res?.unitType.area]);

    const [form, updateForm] = useImmer<Record<string, string>>({
        location: location.locationCode,
        tenant_commercial: function () {
            if (persona && selectionTypes) {
                const personaData = selectionTypes.find((type) => type._id === persona);
                return personaData?.customerType === 'business' ? 'true' : 'false';
            }
            if (cookies.selectedCustomerType === 'business') {
                return 'true';
            }
            return 'false'
        }(),
        company: '',
        companyNumber: '',
        title: 'Mr',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        postcode: '',

        title_alternate: 'Mr',
        firstName_alternate: '',
        lastName_alternate: '',
        email_alternate: '',
        phone_alternate: '',
        address_alternate: '',
        city_alternate: '',
        state_alternate: '',
        postcode_alternate: '',

        driverLicenseNumber: '',
        driverLicenseState: '',
        dateOfBirth: '',
        idDocumentNumber: '',

        insuranceCoverage: '',

        neededDate: getDefaultNeededDate(location, nowDate).toISOString(),

        termsAndConditionsAgree: 'false',
        marketingAgree: 'false',
        declaredValueOfGoodsAgree: 'false',
        insuranceType: 'flatRate',
        declaredValueOfGoods: '',

        pickupLocation: '',

        boxShopCouponId: '',

        password: '',
        confirmPassword: '',

        cardHolderName: '',
        cardNumber: '',
        cardExpiryMonth: '',
        cardExpiryYear: '',
        cardCsv: '',

        autobillAccountNumber: '',
        autobillBsbNumber: '',
        autobillAccountType: '1',

        autobillCreditCardExpiryMonth: '',
        autobillCreditCardExpiryYear: '',
        autobillCreditCardNumber: '',
        autobillCreditCardCsv: '',
        autobillCreditCardName: '',

        autobillType: `${location.rapidstor.autoBillCheckedByDefault
            ? location.rapidstor.directDebit?.enabled
                ? 'dd'
                : 'cc'
            : ''
            }`,
        autobillUsePaymentDetails: 'false',
    });

    /*
    This is similar in functionality (but without validation) to the one in the ReservationService, but because
    the payload is different and ReservationService is a class, it's not possible to reuse.
    @TODO: refactor the function in ReservationService to be more generic and reusable, or create
    a new function in a more generic location.
    */
    const buildAutobillObject = (form: Record<string, string>): MoveInAutoBillPayload => {
        let autoBillType = AutoBillType.None;

        if (location.rapidstor.useAutoBilling) {
            if (form.autobillType === 'cc') {
                autoBillType = AutoBillType.CreditCard;
            } else if (form.autobillType === 'dd') {
                autoBillType = AutoBillType.DirectDebit;
            } else {
                autoBillType = AutoBillType.None;
            }
        }

        const autoBillObject: MoveInAutoBillPayload = {
            usePaymentDetails: form.autobillUsePaymentDetails === 'true' &&
                autoBillType === AutoBillType.CreditCard
                ? true
                : false, // Currently only allow card payments for move-in
            type: autoBillType,
            enabled: autoBillType > AutoBillType.None
        };

        // Check if using a payment gateway, if true we don't use autobill
        if (location.rapidstor.paymentGateways.use) {
            autoBillObject.type = AutoBillType.None;

            return autoBillObject;
        }

        if (autoBillObject.type === AutoBillType.CreditCard) {
            if (form.autobillUsePaymentDetails === 'true') {
                Logger.info('Using existing credit card details', location);
                autoBillObject.expiryMonth = form.cardExpiryMonth;
                autoBillObject.expiryYear = moment(form.cardExpiryYear, 'YYYY').format('YYYY');
                autoBillObject.number = form.cardNumber;
                autoBillObject.cvv = form.cardCsv;
                autoBillObject.name = form.cardHolderName;
            } else {
                Logger.info('Using new credit card details', location);
                autoBillObject.expiryMonth = form.autobillCreditCardExpiryMonth;
                autoBillObject.expiryYear = moment(form.autobillCreditCardExpiryYear, 'YYYY').format('YYYY');
                autoBillObject.number = form.autobillCreditCardNumber;
                autoBillObject.cvv = form.autobillCreditCardCsv;
                autoBillObject.name = form.autobillCreditCardName;
            }
        } else if (autoBillObject.type === AutoBillType.DirectDebit) {
            Logger.info('Using direct debit details', location);
            autoBillObject.accountNumber = form.autobillAccountNumber;
            autoBillObject.bsbNumber = form.autobillBsbNumber;
            autoBillObject.accountType = form.autobillAccountType;
        }

        return autoBillObject;
    };

    const [validationErrors, setValidationErrors] = useState<WizardValidationError[]>([]);

    const { cart, initiateCart, updateCartQuantity } = useCart();
    const [boxShopDiscount, setBoxShopDiscount] = useState<number>(0);

    const [, updateFiles] = useImmer<Record<string, File | null>>({
        driversLicenseFile: null,
        proofOfAddressFile: null,
        proofOfInsuranceFile: null,
    });

    const updateFile = (key: string, file: File | null) => {
        updateFiles((draft) => {
            draft[key] = file;
            return draft;
        });
    };

    const updateDate = (key: string, date: Date) => {
        updateForm((draft) => {
            draft[key] = date.toISOString();
            return draft;
        });
    };

    const updateNeededDate = (date: Date) => {
        updateForm((draft) => {
            draft.neededDate = date.toISOString();
            return draft;
        });

        updateReservation(true);
    };

    const update = (key: string, event: React.BaseSyntheticEvent) => {
        updateForm((draft) => {
            if (key === 'cardNumber') {
                const value = String(event.target.value).replace(' ', '');
                draft[key] = value;
            } else {
                draft[key] = String(event.target.value);
            }
            return draft;
        });
    };

    const updateExpiryDate = (
        value: Record<string, string | number>,
        autobill?: boolean
    ) => {
        if (!value.floatValue) {
            updateForm((draft) => {
                if (autobill) {
                    draft.autobillCreditCardExpiryMonth = '';
                    draft.autobillCreditCardExpiryYear = '';
                } else {
                    draft.cardExpiryMonth = '';
                    draft.cardExpiryYear = '';
                }
                return draft;
            });
        } else {
            updateForm((draft) => {
                if (autobill) {
                    draft.autobillCreditCardExpiryMonth = String(
                        value.formattedValue
                    ).split('/')[0];
                    draft.autobillCreditCardExpiryYear = String(
                        value.formattedValue
                    ).split('/')[1];
                } else {
                    draft.cardExpiryMonth = String(value.formattedValue).split('/')[0];
                    draft.cardExpiryYear = String(value.formattedValue).split('/')[1];
                }
                return draft;
            });
        }
    };

    const populateContactFormData = () => {
        if (res) {
            updateForm((draft) => {
                draft.firstName = res.firstName;
                draft.lastName = res.lastName;
                draft.email = res.email;
                draft.phone = res.phone;
                draft.address = res.addr1 ?? '';
                draft.city = res.city ?? '';
                if (RapidStorSettings(location).state().show()) {
                    draft.state = res.state ?? '';
                }
                if (RapidStorSettings(location).postalCode().show()) {
                    draft.postcode = res.postalCode ?? '';
                }

                // we have this check here to stop issues coming back to the move-in page after being on the move-in confirmed page once.
                // moveInContext doesn't get returned by the API call after the move-in has been finalised.
                if(res.moveInContext) {
                    draft.insuranceCoverage = res.moveInContext.movein.insuranceCoverage ?? '';
                    if (RapidStorSettings(location).alternateContact().show()) {
                        draft.firstName_alternate = res.moveInContext.tenant.alternativeContact.firstName ?? '';
                        draft.lastName_alternate = res.moveInContext.tenant.alternativeContact.lastName ?? '';
                        draft.email_alternate = res.moveInContext.tenant.alternativeContact.email ?? '';
                        draft.phone_alternate = res.moveInContext.tenant.alternativeContact.phone ?? '';
                        draft.address_alternate = res.moveInContext.tenant.alternativeContact.address ?? '';
                        draft.city_alternate = res.moveInContext.tenant.alternativeContact.city ?? '';
                        if (RapidStorSettings(location).state().show()) {
                            draft.state_alternate = res.moveInContext.tenant.alternativeContact.state ?? '';
                        }
                        if (RapidStorSettings(location).postalCode().show()) {
                            draft.postcode_alternate = res.moveInContext.tenant.alternativeContact.postalCode ?? '';
                        }
                    }
                }
                return draft;
            });
        }
    };

    const updateCartItem = (id: string, quantity: number) => {
        const result = updateCartQuantity(id, quantity);
        if (result) {
            setProductRemoval(result);
        }
    };

    const formErrors = useMemo(() => {
        const inputName: string[] = ['termsAndConditionsAgree'];

        return validationErrors?.filter((error) => inputName.includes(error.name));
    }, [validationErrors]);

    const showProductRemovalModal = useMemo(() => {
        return productRemoval !== null;
    }, [productRemoval]);

    useEffect(() => {
        if (
            moveInWizardDataInit?.reservation.finalized &&
            searchParams.get('error') !== 'true'
        ) {
            const completionRedirectUrl = location.rapidstor.jblRedirects.afterMoveIn.url;
            const useCompletionRedirect = location.rapidstor.jblRedirects.afterMoveIn.enabled;

            if (useCompletionRedirect && completionRedirectUrl) {
                window.location.replace(completionRedirectUrl);
            } else {
                navigate(`/move-in-confirmed/${res?.token}`);
            }
        } else if (
            moveInWizardDataInit?.reservation.finalized &&
            searchParams.get('error') === 'true'
        ) {
            navigate(`/move-in-confirmed/${res?.token}?error=true&message=${searchParams.get('message')}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [moveInWizardDataInit]);

    useEffect(() => {
        const setup = async () => {
            setCostRetrieve(undefined);
            const result = await fetchCostRetrieve();
            setCostRetrieve(result);
            updateForm((draft) => {
                if (draft.lastName === '.') {
                    draft.lastName = '';
                }
            });
        };

        if (res && res.token && !costRetrieve) {
            setup();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [res]);

    const selectedInsuranceCoverage = useMemo(() => {
        if (form.insuranceCoverage != '') {
            return availableInsuranceCoverage?.find(
                (coverage) => coverage._id === form.insuranceCoverage
            );
        }

        if (reservation?.moveInContext?.movein.insuranceCoverage) {
            return availableInsuranceCoverage?.find(
                (coverage) => {
                    if (typeof reservation?.moveInContext.movein.insuranceCoverage === 'string') {
                        return coverage._id === reservation?.moveInContext.movein.insuranceCoverage;
                    }

                    if (typeof reservation?.moveInContext.movein.insuranceCoverage === 'number') {
                        return coverage.InsurCoverageID === reservation?.moveInContext.movein.insuranceCoverage;
                    }

                    return false;
                }
            );
        }

        return undefined;
    }, [availableInsuranceCoverage, form.insuranceCoverage, reservation?.moveInContext?.movein.insuranceCoverage]);

    useEffect(() => {
        if (searchParams.get('error') === 'true') {
            const error = `An error occurred during the move in process\n CODE: ${searchParams.get(
                'code'
            )}\n MESSAGE: ${searchParams.get('message')}`;
            firebaseLogEvent('move_in_sca_error', {});
            Logger.error(`[SCAMoveIn] Error: ${error}`, location, [
                {
                    name: 'rapidstor_reservationToken',
                    value: `${res?.token}`,
                },
                {
                    name: 'rapidstor_corpToken',
                    value: `${window.rapidstor_settings.rapidstor_options.corpToken}`,
                },
                {
                    name: 'sca_error_code',
                    value: `${searchParams.get('code')}`,
                },
                {
                    name: 'sca_error_message',
                    value: `${searchParams.get('message')}`,
                },
            ]);
            const deconstructedError = deconstructScaErrorMessage(error);
            if (!deconstructedError) {
                if (window.rapidstor_settings.app_config.journey.dev) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                }
                setMoveInError(t(`errors:10000`));
            } else {
                setMoveInError(t(`errors:${deconstructedError.reasonCode}`));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, res?.token, searchParams]);

    useEffect(() => {
        const setupCart = () => {
            const lineItems: LineItem[] = [];

            moveInWizardMerchandise?.products.forEach((product) => {
                if (product.visible && !product.deleted) {
                    lineItems.push({
                        _id: product._id,
                        name: product.name,
                        key: `${product._id}_product_idx`,
                        price: product.price.priceBeforeTax,
                        displayPrice: product.price.displayPrice,
                        tax: product.price.tax,
                        inStock: product.inStock,
                        inventory: product.inventory.dcInStock,
                        picture: product.picture,
                        quantity: product.defaultSelected ? 1 : 0,
                        featured: product.featured,
                        featuredOrder: product.featuredOrder,
                        defaultSelected: product.defaultSelected,
                        removedText: product.removedText,
                        removedImage: product.removedImage,
                    });
                }
            });

            lineItems.sort((a, b) => {
                if (a.featured && b.featured && a.featuredOrder && b.featuredOrder) {
                    return a.featuredOrder - b.featuredOrder;
                } else if (a.featured) {
                    return -1;
                } else if (b.featured) {
                    return 1;
                } else {
                    return 0;
                }
            });

            initiateCart(lineItems);
        };

        if (merchandiseSuccess) {
            setupCart();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchandiseSuccess, moveInWizardMerchandise]);

    /**
     * @TODO Migrate this into JourneyContext
     */
    const checkCookie = () => {
        if (reservation) {
            setCookie(
                'reservation',
                {
                    token: reservation.token,
                    id: reservation._id,
                },
                {
                    path: window.location.pathname,
                    maxAge: 60 * 60 * 24 * 7,
                }
            );
        }
    };

    /**
     * @TODO Shouldn't need to even have this.
     */
    const submitPayment = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    };

    /**
     * @TODO Create a useForm hook to handle all this form crap.
     */
    const validateForm = async (): Promise<WizardValidationError[]> => {
        setValidationErrors([]);
        const errors: WizardValidationError[] = [];
        const excludeKeys: string[] = [];

        if (window.rapidstor_settings.app_config.journey.optionalMoveInFields) {
            excludeKeys.push(...window.rapidstor_settings.app_config.journey.optionalMoveInFields);
        }

        if (form.tenant_commercial === 'false') {
            excludeKeys.push(...['company', 'companyNumber']);
        }

        if (reservation?.unitType.excludeFromInsurance || availableInsuranceCoverage?.length === 0) {
            excludeKeys.push(...[
                'insuranceCoverage',
                'declaredValueOfGoodsAgree',
                'declaredValueOfGoods',
            ]);
        }

        const excludedInputKeys = excludedInputs(location);
        const formKeys = Object.keys(form).filter(
            (key) => !excludedInputKeys.includes(key) && !excludeKeys.includes(key)
        );

        for (const key of formKeys) {
            let normalisedKey = normaliseFieldName(key);

            const getCorpTranslationsResult = getCorpTranslations(location.corpCode as CorporationCode);

            const isWizardTranslationsSet = getCorpTranslationsResult && 'wizard' in getCorpTranslationsResult;

            if (key === 'insuranceCoverage') {
                if (isWizardTranslationsSet && 'insuranceCoverage' in getCorpTranslationsResult.wizard) {
                    normalisedKey = getCorpTranslationsResult.wizard.insuranceCoverage.title;
                }
            }

            if (!validateString(form[key])) {
                errors.push({
                    name: key,
                    message: `${normalisedKey} is required`,
                });
            }

            if (key === 'email' && !validateEmail(form[key])) {
                errors.push({
                    name: key,
                    message: 'Email is invalid',
                });
            }

            if (key === 'termsAndConditionsAgree' && form[key] === 'false') {
                errors.push({
                    name: key,
                    message: 'Terms and Conditions must be agreed to continue',
                });
            }

            // Not stuffing all this logic into one if statement.
            const isDeclaredValueOfGoodsAgreeValid = (): boolean => {
                if ((
                    RapidStorSettings(location).insurance().required() ||
                    RapidStorSettings(location).insurance().manualEntry().required()
                ) && form.declaredValueOfGoodsAgree === 'false') {
                    return false;
                }

                if (
                    RapidStorSettings(location).insurance().optional() &&
                    form.insuranceCoverage &&
                    form.declaredValueOfGoodsAgree === 'false' &&
                    location.rapidstor.wizards.moveinSingle.insuranceRefusedText
                ) {
                    return false;
                }

                return true;
            }

            if (key === 'declaredValueOfGoodsAgree' && !isDeclaredValueOfGoodsAgreeValid()) {
                errors.push({
                    name: key,
                    message: 'You must agree to the statement below to continue',
                });
            }

            if (key === 'declaredValueOfGoods' &&
                RapidStorSettings(location).insurance().manualEntry().required() &&
                (form.declaredValueOfGoods === '' || form.declaredValueOfGoods === '0')
            ) {
                errors.push({
                    name: key,
                    message: 'Declared Value of Goods must be larger than 0',
                });
            }
        }

        if (RapidStorSettings(location).passwordFields().show()) {
            if (form.password !== form.confirmPassword) {
                errors.push({
                    name: 'passwordMissmatch',
                    message: 'Passwords do not match',
                });
            }

            if (!validatePassword(form.password) || !validatePassword(form.confirmPassword)) {
                errors.push({
                    name: 'passwordInvalid',
                    message: 'Password Invalid',
                });
            }
        }

        if (RapidStorSettings(location).paymentForm().show()) {
            const cc = creditCardType(form.cardNumber);

            if (!validateCreditCardNumber(form.cardNumber, location) || !cc) {
                errors.push({
                    name: 'cardNumber',
                    message: 'Invalid card number',
                });
            }

            const formattedExpiryMonth = moment(form.cardExpiryMonth, 'MM').format('MM');
            const formattedExpiryYear = moment(form.cardExpiryYear, 'YYYY').format('YYYY');
            const lastDayOfMonth = moment(
                `${formattedExpiryYear}-${formattedExpiryMonth}`
            ).endOf('month');
            const today = moment();

            if (lastDayOfMonth < today) {
                errors.push({
                    name: 'cardExpiry',
                    message: 'Card has expired',
                });
            }

            if (cc && !validateCreditCardCSV(cc, form.cardCsv)) {
                errors.push({
                    name: 'cardCsv',
                    message: 'Invalid CSV',
                });
            }
        }

        if (location.rapidstor.useAutoBilling && form.autobillType !== '') {
            const fieldsToValidate = getAutobillFields(
                location,
                form.autobillType === 'cc',
                form.autobillUsePaymentDetails === 'true'
            );

            for (const field of fieldsToValidate) {
                if (field === 'bank') {
                    continue;
                }
                if (!validateString(form[field])) {
                    errors.push({
                        name: field,
                        message: `${normaliseFieldName(field)} is required`,
                    });
                }
            }
        }

        setValidationErrors(errors);

        if (window.rapidstor_settings.rapidstor_options.dev) {
            // eslint-disable-next-line no-console
            console.error(errors);
        }

        return errors;
    };

    const confirmBookingButtonText = (confirmText?: string): string => {
        return t(`wizard:${confirmText || 'confirm_booking'}`);
    };

    const setAddressUsingLoqate = async (address: LoqateAddressFindResponseItem, alternate?: boolean) => {
        const loqate = new LoqateValidator(form, location);

        if (address.Type === 'Address') {
            const result = await loqate.retrieveAddress(address.Id);
            if (result) {
                updateForm((draft) => {
                    if (alternate) {
                        draft.address_alternate = result.Line1;
                        draft.city_alternate = result.City;
                        draft.state_alternate = result.Province;
                        draft.postcode_alternate = result.PostalCode;
                    } else {
                        draft.address = result.Line1 + ' ' + result.Line2 + ' ' + result.Line3 + ' ' + result.Line4;
                        draft.city = result.City;
                        draft.state = result.Province;
                        draft.postcode = result.PostalCode;
                    }
                    return draft;
                });
            }
        } else {
            const containerResult = await loqate.validateResidential(address.Text, 'Address', address.Id, alternate);

            if (containerResult) {
                setValidationErrors((prev) => prev.filter((e) => e.name !== (alternate ? 'address_alternate' : 'address')));
                if (alternate) {
                    setLoqateAddressResults({ main: [], alternate: containerResult });
                } else {
                    setLoqateAddressResults({ main: containerResult, alternate: [] });
                }
                setLoqateLoading(undefined);
            } else {
                updateForm((draft) => {
                    if (alternate) {
                        draft.postcode_alternate = address.Text;
                    } else {
                        draft.postcode = address.Text;
                    }
                });
            }
        }
    };

    const loqateValidation = async (fieldName: string, value?: string, alternate?: boolean) => {
        if (!window.rapidstor_settings.rapidstor_options.loqate) {
            return;
        }

        if (!value && fieldName !== 'bank') {
            setLoqateAddressResults({ main: [], alternate: [] });
            setLoqateLoading(undefined);
            return;
        }

        if (fieldName !== 'address') {
            setLoqateLoading(fieldName as LoqateValidationFields);
        }

        const loqate = new LoqateValidator(form, location);
        const addressFieldsToValidate = alternate ? [form.address_alternate, form.city_alternate] : [form.address, form.city];

        if (RapidStorSettings(location).state().show()) {
            addressFieldsToValidate.push(alternate ? form.state_alternate : form.state);
        }

        if (RapidStorSettings(location).postalCode().show()) {
            addressFieldsToValidate.push(alternate ? form.postcode_alternate : form.postcode);
        }

        const bankFieldsToValidate = [form.autobillBsbNumber, form.autobillAccountNumber];

        if (fieldName === 'bank') {
            for (const field of bankFieldsToValidate) {
                if (!validateString(field)) {
                    setLoqateLoading(undefined);
                    return;
                }
            }
        }

        switch (fieldName) {
            case 'email':
                try {
                    await loqate.validateEmail(form.email);
                    setLoqateLoading(undefined);
                } catch (error) {
                    setLoqateLoading(undefined);
                }
                break;
            case 'address':
                try {
                    const results = await loqate.validateResidential(
                        addressFieldsToValidate.join(', '),
                        'Address',
                        undefined,
                        alternate
                    );

                    if (results !== false) {
                        setLoqateAddressResults(alternate ? { main: [], alternate: results } : { main: results, alternate: [] });
                    } else {
                        setLoqateAddressResults({ main: [], alternate: [] });
                    }

                    setLoqateLoading(undefined);
                } catch (error) {
                    Logger.warn(
                        `[loqateValidation] Failed validating address: ${error}`,
                        location,
                        [
                            {
                                name: 'address_entry',
                                value: addressFieldsToValidate.join(', '),
                            },
                        ]
                    );
                    setLoqateLoading(undefined);
                }
                break;
            case 'state':
            case 'postcode':
                try {
                    const results = await loqate.validateResidential(
                        addressFieldsToValidate.join(', '),
                        'Postcode',
                        undefined,
                        alternate
                    );

                    if (results !== false) {
                        setLoqateAddressResults(alternate ? { main: [], alternate: results } : { main: results, alternate: [] });
                    } else {
                        setLoqateAddressResults({ main: [], alternate: [] });
                    }

                    setLoqateLoading(undefined);
                } catch (error) {
                    Logger.warn(
                        `[loqateValidation] Failed validating postcode: ${error}`,
                        location,
                        [
                            {
                                name: 'address_entry',
                                value: addressFieldsToValidate.join(', '),
                            },
                        ]
                    );
                    setLoqateLoading(undefined);
                }
                break;
            case 'bank':
                try {
                    const result = await loqate.validateBank(
                        form.autobillBsbNumber,
                        form.autobillAccountNumber
                    );
                    if (result) {
                        setBankName(result.Bank);
                    } else {
                        setBankName('');
                    }
                    setLoqateLoading(undefined);
                } catch (error) {
                    setBankName('');
                    setLoqateLoading(undefined);
                }
                break;
        }

        if (loqate.errors.length) {
            for (const error of loqate.errors) {
                if (!validationErrors.find((e) => e.name === error.name)) {
                    setValidationErrors((prev) => [...prev, error]);
                }
            }
        } else {
            setValidationErrors((prev) => prev.filter((e) => e.name !== fieldName));
        }
    };

    /**
     * @TODO Pretty sure this should be at the top with the other mutations and have a refetch method
     */
    const fetchCostRetrieve = async () => {
        try {
            const result = await moveInCostRetrieve({
                location: location.locationCode,
                corpCode: location.corpCode,
                reservationId: res?.token,
            }).unwrap();

            return result;
        } catch (error) {
            Logger.error('[fetchCostRetrieve] Failed fetching cost retrieve', location, [
                {
                    name: 'rapidstor_reservationToken',
                    value: `${res?.token}`,
                },
            ]);

            return undefined;
        }
    };

    const updateReservation = async (doCostRetrieve: boolean, insuranceCoverage?: string) => {
        if (!reservation?.token) {
            Logger.info('No reservation token found', location);
            return;
        }

        if (doCostRetrieve) {
            setCostRetrieve(undefined);
        }

        /**
         * @TODO Create a method in useCart for getting the cart itemns to add to the reservation
         */
        const cartItems: Record<string, number> = {};
        cart.map((item) => {
            if (item.quantity > 0) {
                cartItems[item._id] = item.quantity;
            }
        });

        const reservationSvc = new ReservationService(location);

        try {
            const payload = reservationSvc.buildPreMoveInPatchPayload(
                reservation,
                { ...form, insuranceCoverage: insuranceCoverage ?? form.insuranceCoverage },
                cartItems,
                {},
                location
            );
            const patchResult = await patchReservation(payload).unwrap();

            setReservation(patchResult);

            if (doCostRetrieve) {
                /**
                 * @TODO Can't remember why this needs to get set undefined first. Try removing it.
                 */
                setCostRetrieve(undefined);
                const result = await fetchCostRetrieve();
                setCostRetrieve(result);
            }

            return patchResult;
        } catch (error) {
            if (error instanceof Error) {
                if (error.message === 'Invalid credit card number') {
                    const errors = validationErrors;
                    errors.push({
                        name: 'cardNumber',
                        message: 'Invalid credit card number',
                    });
                    setValidationErrors(errors);
                }
            } else {
                Logger.error(
                    `Failed updating reservation: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]
                );
            }
        }
    };

    const processBooking = async (payment_method?: PaymentProcessor) => {
        firebaseLogEvent('move_in_button_clicked', {});
        setMoveInError(undefined);
        setMoveInLoading(true);
        const errors = await validateForm();

        if (errors.length > 0) {
            firebaseLogEvent('move_in_validation_error', {});
            setMoveInError('Please fill in all required fields');
            setMoveInLoading(false);
            return;
        }

        const availableResult = await handleUnitAvailability();

        if (!availableResult) {
            return;
        }


        const updatedReservation = await updateReservation(false);
        if (!updatedReservation || !res?._id) {
            setMoveInError('Failed updating reservation');
            setMoveInLoading(false);
            return;
        }

        if (
            location.rapidstor.paymentGateways.use &&
            location.rapidstor.paymentGateways.gateways.r6Pay &&
            updatedReservation
        ) {
            const returnUrl = (): string => {
                return `https://${window.location.host}${window.location.pathname}${window.location.search}#/api/payment-redirect/${updatedReservation?.token}/move-in`;
            };

            const payload = r6PayService.service.buildCreatePaymentLinkPayload(
                updatedReservation,
                form,
                location,
                location.rapidstor.paymentGateways.gateways.r6Pay.currency,
                returnUrl(),
                payment_method ?? PaymentProcessor.FINVERSE,
            );

            try {
                const result = await r6PayService.service.createPaymentLink(payload);
                if (!result) {
                    Logger.error(`[processBooking] Failed R6Pay request`, location, [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        }
                    ]);
                    setMoveInError(`R6Pay Create Payment Error`);
                    setMoveInLoading(false);
                    return;
                }
                if (result.next_action.type === NextAction.REDIRECT_TO_URL) {
                    window.location.href = result.next_action.url;
                    return;
                }
            } catch (error) {
                Logger.error(
                    `[processBooking] Failed R6Pay request: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]
                );
                setMoveInError(`R6Pay Create Payment Error`);
                setMoveInLoading(false);
                return;
            }
        }

        if (Object.keys(location.scaConfiguration).length > 0) {
            try {
                const result = await makeScaMoveInPrepare({
                    locationCode: location.locationCode,
                    corpCode: location.corpCode,
                    payload: {
                        cardHoldersName: form.cardHolderName,
                        cardNumber: form.cardNumber,
                        cardExpiryMonth: form.cardExpiryMonth,
                        cardExpiryYear: form.cardExpiryYear,
                        cardCVV: form.cardCsv,
                        cardTypeId: creditCardType(form.cardNumber),
                        reservationId: res?._id,
                        view: 'standalone',
                        parentUri: window.location.href,
                        standaloneReturnUrl: `${window.location.protocol}//${window.location.host}${window.location.pathname}${window.location.search}#/api/payment-redirect/${res?.token}/move-in`,
                    },
                }).unwrap();

                window.location.href = result.redirect;
            } catch (error) {
                firebaseLogEvent('move_in_sca_error', {});
                Logger.error(
                    `[processBooking] Failed SCA move in request: ${JSON.stringify(
                        error
                    )}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]
                );
                setMoveInError(`${JSON.stringify(error)}`);
                setMoveInLoading(false);
            }
        } else {
            try {
                const result = await makeMoveIn({
                    locationCode: location.locationCode,
                    corpCode: location.corpCode,
                    payload: {
                        ...form,
                        autobill: buildAutobillObject(form),
                        card: {
                            name: form.cardHolderName,
                            type: creditCardType(form.cardNumber),
                            number: form.cardNumber,
                            cvv: form.cardCsv,
                            expiryMonth: moment(form.cardExpiryMonth, 'MM').format('MM'),
                            expiryYear: moment(form.cardExpiryYear, 'YYYY').format('YYYY'),
                        },
                    },
                    token: reservation?.token,
                }).unwrap();

                if (result.operation_result === 'success') {
                    setMoveInError('');
                    navigate(`/api/payment-redirect/${res?.token}/move-in`);
                } else {
                    firebaseLogEvent('move_in_error', {});
                    Logger.error(
                        `[processBooking] Failed move in request: ${JSON.stringify(
                            result
                        )}`,
                        location
                    );
                    setMoveInError(`${JSON.stringify(result.operation_message)}`);
                    setMoveInLoading(false);
                }
            } catch (error) {
                Logger.error(
                    `[processBooking] Failed move in request: ${JSON.stringify(error)}`,
                    location,
                    [
                        {
                            name: 'rapidstor_reservationToken',
                            value: `${res?.token}`,
                        },
                    ]
                );
                firebaseLogEvent('move_in_error', {});

                if (typeof error === "string") {
                    setMoveInError(error);
                } else {
                    setMoveInError(`${JSON.stringify(error)}`);
                }
                setMoveInLoading(false);
            }
        }
    };

    /**
     * @TODO this can go into the useForm hook or whatever gets created to manage that state
     */
    const fillForm = async () => {
        updateForm((draft) => {
            draft.title = 'Mr';
            draft.firstName = 'Test';
            draft.lastName = 'User';
            draft.email = 'test@test.com';
            draft.phone = '123123123123';
            draft.address = '123 Test St';
            draft.city = 'Test City';
            draft.state = 'Test State';
            draft.postcode = '1234';

            if (RapidStorSettings(location).alternateContact().show()) {
                draft.title_alternate = 'Mrs';
                draft.firstName_alternate = 'Alternate';
                draft.lastName_alternate = 'User';
                draft.email_alternate = 'test@test.com';
                draft.phone_alternate = '123123123123';
                draft.address_alternate = '123 Test St';
                draft.city_alternate = 'Test City';
                draft.state_alternate = 'Test State';
                draft.postcode_alternate = '1234';
            }

            draft.password = 'ValidPassword1';
            draft.confirmPassword = 'ValidPassword1';

            if (RapidStorSettings(location).driversLicense().show()) {
                draft.driverLicenseNumber = '123456789';
                draft.dateOfBirth = moment().format("YYYY-MM-DD");
                draft.idDocumentNumber = '123456789';

                if (!location.rapidstor.wizards.moveinSingle.hideDriversLicenceState) {
                    draft.driverLicenseState = 'VIC';
                }
            }

            if (RapidStorSettings(location).insurance().manualEntry().required()) {
                draft.declaredValueOfGoodsAgree = 'true';
                draft.declaredValueOfGoods = '1000';
            }

            if (RapidStorSettings(location).dateOfBirth().show()) {
                draft.dateOfBirth = moment().subtract(18, 'years').format('YYYY-MM-DD');
            }

            draft.cardHolderName = 'test user';
            draft.cardNumber = '4242424242424242';
            draft.cardExpiryMonth = '12';
            draft.cardExpiryYear = `${new Date().getFullYear() + 1}`.slice(-2);
            draft.cardCsv = '123';
        });
    };

    const getInitialSelectedInsuranceCoverage = (): string => {
        if (res?.unitType.descriptor.defaultInsuranceCoverage && res?.moveInContext.movein.insuranceCoverage === null && moveInWizardDataInit?.siteInsuranceCoverage.some((coverage) => coverage._id === res?.unitType.descriptor.defaultInsuranceCoverage)) {
            return res?.unitType.descriptor.defaultInsuranceCoverage;
        }

        if (res?.moveInContext.movein.insuranceCoverage && res?.moveInContext.movein.insuranceCoverage !== '') {
            const found = availableInsuranceCoverage?.find((coverage) => {
                if (typeof res?.moveInContext.movein.insuranceCoverage === 'string') {
                    return coverage._id === res?.moveInContext.movein.insuranceCoverage;
                } else if (typeof res?.moveInContext.movein.insuranceCoverage === 'number') {
                    return coverage.InsurCoverageID === res?.moveInContext.movein.insuranceCoverage;
                } else {
                    return false;
                }
            });
            if (found) {
                return found._id.toString();
            }
        }

        return '';
    };

    const initiateR6Pay = async () => {
        await r6PayService.service?.getPaymentMethods();
    };

    useEffect(() => {
        if (res) {
            setReservation(res);
            populateContactFormData();
            if (moveInInitSuccess) {
                updateForm((draft) => {
                    draft.insuranceCoverage = getInitialSelectedInsuranceCoverage();
                    if (draft.insuranceCoverage && RapidStorSettings(location).insurance().manualEntry().show()) {
                        const selectedInsuranceCoverage = availableInsuranceCoverage?.find((coverage) => coverage._id === draft.insuranceCoverage);
                        draft.declaredValueOfGoods = selectedInsuranceCoverage ? `${selectedInsuranceCoverage.dcCoverage}` : '';
                    }
                    return draft;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSuccess, res, moveInInitSuccess]);

    useEffect(() => {
        if (!reservationIdentifier || !window.rapidstor_settings.rapidstor_options.corpToken) {
            navigate('/');
        } else {
            setReservation(res);
            checkCookie();

            if (
                location.rapidstor.paymentGateways.use &&
                location.rapidstor.paymentGateways.gateways.r6Pay &&
                !r6PayService.service
            ) {
                initiateR6Pay();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (reservation && unitList) {
            Logger.info(`[MoveIn] Got reservation and unitList data`, location);

            const [foundDescriptor, foundUnitType] = unitList.descriptors.reduce<
                [Descriptor | null, UnitType | null]
            >(
                (acc, descriptor) => {
                    const found = descriptor.unitTypes.find((uT: UnitType) => {
                        return uT._id === reservation.unitType._id;
                    });
                    if (found) {
                        return [descriptor, found];
                    }
                    return acc;
                },
                [null, null]
            );

            if (foundDescriptor && foundUnitType) {
                Logger.info(`[MoveIn] Found matching unitType`, location, []);

                if (reservation.persona) {
                    const unitTypePersona: UnitType = {
                        ...foundUnitType,
                        rates: {
                            ...foundUnitType.rates,
                            rate: reservation.rates.rate,
                            discounted: reservation.rates.discounted,
                            discountedRate: reservation.rates.discountedRate ?? 0,
                        },
                    };

                    const descriptorPersona: Descriptor = {
                        ...foundDescriptor,
                        name: reservation.unitType.descriptor.name,
                        tags: reservation.unitType.descriptor.tags,
                        description: reservation.unitType.descriptor.description,
                        descriptions: reservation.unitType.descriptor.descriptions,
                        specialText: reservation.unitType.descriptor.specialText,
                    };
                    setUnitType(unitTypePersona);
                    setDescriptor(descriptorPersona);
                    if(reservation.persona._id) {
                        store.dispatch(
                            setPersonaId(reservation.persona?._id)
                        );
                    }

                    setupSliceData({
                        unitType: unitTypePersona,
                        descriptor: descriptorPersona,
                        dealObject: reservation.deal,
                        deal: reservation.deal?._id,
                        token: reservation.token,
                    }, {
                        location,
                        unitType: unitTypePersona,
                        descriptor: descriptorPersona,
                        descriptors: unitList.descriptors,
                    });
                } else {
                    setUnitType(foundUnitType);
                    setDescriptor(foundDescriptor);

                    setupSliceData({
                        unitType: foundUnitType,
                        descriptor: foundDescriptor,
                        dealObject: reservation.deal,
                        deal: reservation.deal?._id,
                        token: reservation.token,
                    }, {
                        location,
                        unitType: foundUnitType,
                        descriptor: foundDescriptor,
                        descriptors: unitList.descriptors,
                    });
                }
                checkCookie();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitList, reservation]);

    /** @todo Temporary fix, will need to improve this error handling process once the stack trace errors are fixed on rapidstor api */
    if (moveInCostRetrieveError) {
        return (
            <ErrorComponent
                customError={moveInCostRetrieveError as string}
                location={location}
            />
        )
    }

    return (
        <div
            className={`${classes.container} ${moveInLoading && classes.disabledContainer
                }`}
        >
            <div className={`${classes.header}`}>
                <div>
                    <Button
                        onclick={() => {
                            fadeOut();
                            firebaseLogEvent('move_in_back_button_clicked', {});
                            setTimeout(() => {
                                const backUrl =
                                    window.rapidstor_settings.app_config.pages.bookUnit === 'map'
                                        ? '/book-unit'
                                        : '/customise-booking';
                                navigate(backUrl);
                            }, 300);
                        }}
                        type={'primaryOnWhite'}
                        size={'medium'}
                        chevron={'left'}
                    >
                        <span>{t('wizard:back')}</span>
                    </Button>
                </div>
                <span className={`${classes.title}`}>{t('wizard:scheduleMoveIn')}</span>
                <div></div>
            </div>

            {moveInInitLoading || !moveInInitSuccess ? (
                <>
                    <Spinner size='100' />
                </>
            ) : (
                <>
                    <WizardDescriptor
                        descriptor={descriptor}
                        unitType={unitType}
                        deal={reservation?.deal}
                    />

                    <div className={`${classes.fold}`}></div>

                    {moveInError && <Alert type={'error'} text={moveInError} />}

                    <ContactDetailsForm
                        form={form}
                        update={update}
                        updateForm={updateForm}
                        updateDate={updateDate}
                        updateNeededDate={updateNeededDate}
                        validationErrors={validationErrors}
                        loqateValidation={loqateValidation}
                        loqateLoading={loqateLoading}
                        loqateAddressResults={loqateAddressResults}
                        setLoqateAddressResults={setLoqateAddressResults}
                        setAddressUsingLoqate={setAddressUsingLoqate}
                    />

                    {RapidStorSettings(location).alternateContact().show() && (
                        <ContactDetailsForm
                            form={form}
                            update={update}
                            updateDate={updateDate}
                            updateForm={updateForm}
                            updateNeededDate={updateNeededDate}
                            alternate
                            validationErrors={validationErrors}
                            loqateValidation={loqateValidation}
                            loqateLoading={loqateLoading}
                            setAddressUsingLoqate={setAddressUsingLoqate}
                            loqateAddressResults={loqateAddressResults}
                            setLoqateAddressResults={setLoqateAddressResults}
                        />
                    )}

                    <div className={`${classes.fold}`}></div>

                    {RapidStorSettings(location).passwordFields().show() && (
                        <>
                            <AccountDetails
                                password={form.password}
                                confirmPassword={form.confirmPassword}
                                updatePassword={updateForm}
                                validationErrors={validationErrors}
                            />

                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    {RapidStorSettings(location).identityVerification().show() && (
                        <>
                            <IdentityVerification
                                update={update}
                                updateDate={updateDate}
                                updateFile={updateFile}
                                form={form}
                                validationErrors={validationErrors}
                            />
                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    {RapidStorSettings(location).insurance().show(reservation, availableInsuranceCoverage) && (
                        <>
                            <InsuranceCoverageComponent
                                validationErrors={validationErrors}
                                update={updateForm}
                                coverages={availableInsuranceCoverage}
                                currentlySelected={
                                    availableInsuranceCoverage?.findIndex(i => i._id === selectedInsuranceCoverage?._id) ?? -1
                                }
                                form={form}
                                updateReservation={updateReservation}
                                taxRate={moveInWizardDataInit.insuranceCharge.dcTax1Rate + moveInWizardDataInit.insuranceCharge.dcTax2Rate}
                            />
                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    {RapidStorSettings(location).proofOfInsurance().show(form.insuranceType) && (
                        <>
                            <ProofOfInsurance
                                updateFile={updateFile}
                                resTenant={reservation?.tenantId}
                                resToken={reservation?.token}
                                validationErrors={validationErrors}
                            />

                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    {RapidStorSettings(location).proofOfAddress().show() && (
                        <>
                            <ProofOfAddress
                                updateFile={updateFile}
                                resTenant={reservation?.tenantId}
                                resToken={reservation?.token}
                            />

                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    {RapidStorSettings(location).boxshop().show() && (
                        <>
                            <Merchandise
                                cart={cart}
                                pickupLocation={form.pickupLocation}
                                updateCartItem={updateCartItem}
                                updatePickupLocation={(location: string) => {
                                    updateForm((draft) => {
                                        draft.pickupLocation = location;
                                        return draft;
                                    });
                                }}
                                setBoxShopDiscount={(
                                    discount: RapidStorBoxShopCouponResponse
                                ) => {
                                    setBoxShopDiscount(discount.discountPercentage);
                                    updateForm((draft) => {
                                        draft.boxShopCouponId = discount._id;
                                    });
                                }}
                                boxShopDiscount={boxShopDiscount}
                            />
                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    <WizardPricingTable
                        costRetrieve={costRetrieve}
                        charges={undefined}
                        cart={cart}
                        moveIn
                        merchandiseDiscount={boxShopDiscount}
                        style={window.rapidstor_settings.rapidstor_options.pricingTable?.style}
                        showTaxRow={window.rapidstor_settings.rapidstor_options.pricingTable?.showTaxRow}
                    />

                    <div className={`${classes.fold}`}></div>

                    {RapidStorSettings(location).paymentForm().show() && (
                        <>
                            <PaymentForm
                                onSubmit={submitPayment}
                                form={form}
                                submittingPayment={false}
                                update={update}
                                updateExpiryDate={updateExpiryDate}
                                updateForm={updateForm}
                                validationErrors={validationErrors}
                                moveIn
                            />
                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    {RapidStorSettings(location).autobill().show() && (
                        <>
                            <AutoBill
                                form={form}
                                update={updateForm}
                                loqateValidation={loqateValidation}
                                updateExpiryDate={updateExpiryDate}
                                loqateLoading={loqateLoading}
                                validationErrors={validationErrors}
                                bankName={bankName}
                            />
                            <div className={`${classes.fold}`}></div>
                        </>
                    )}

                    <span className={`${classes.header_text}`}>
                        {t('wizard:termsAndConditions')}
                    </span>

                    {moveInWizardDataInit?.siteTermsAndConditions && (
                        <div className={`${classes.terms_and_conditions}`}>
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: moveInWizardDataInit?.siteTermsAndConditions,
                                }}
                            ></p>
                        </div>
                    )}

                    {formErrors
                        .filter((error) => error.name === 'termsAndConditionsAgree')
                        .map((error, index) => (
                            <ErrorTag
                                key={`${index}_error_idx`}
                                error={{
                                    name: error.name,
                                    message: error.message,
                                }}
                            />
                        ))}

                    <div className={`${classes.opt_in_container}`}>
                        <CircleCheckbox
                            value={form.termsAndConditionsAgree}
                            update={updateForm}
                            label={t('wizard:termsAndConditionsAgree')}
                            formKey={'termsAndConditionsAgree'}
                        />
                        <CircleCheckbox
                            value={form.marketingAgree}
                            update={updateForm}
                            label={t('wizard:marketingAgree')}
                            formKey={'marketingAgree'}
                        />
                    </div>

                    {moveInError && <Alert type={'error'} text={moveInError} />}

                    <div className={`${classes.confirm_booking}`}>
                        {!RapidStorSettings(location).paymentForm().show() ? (
                            r6PayService.service?.paymentMethods?.map((method) => (
                                <Button
                                    key={method.id}
                                    disableScroll
                                    onclick={() => {
                                        processBooking(method.type)
                                    }}
                                    type={'primary'}
                                    size={'medium'}
                                    disabled={moveInLoading}
                                    style={{ marginBottom: '12px' }}
                                >
                                    {moveInLoading ? (
                                    <Spinner size='25' inverted />
                                ) : (
                                    <>{confirmBookingButtonText(method.custom_fields.display_name)}</>
                                )}
                                </Button>
                            ))
                        ) :  (
                            <Button
                                disableScroll
                                onclick={processBooking}
                                type={'primary'}
                                size={'medium'}
                                disabled={moveInLoading}
                            >
                                {moveInLoading ? (
                                    <Spinner size='25' inverted />
                                ) : (
                                    <>{confirmBookingButtonText()}</>
                                )}
                            </Button>
                        )}

                        {(window.rapidstor_settings.rapidstor_options.dev || location.administrative.testMode) && (
                            <Button
                                style={{ marginTop: '12px' }}
                                onclick={fillForm}
                                disableScroll
                                type={'outlineOnBackground'}
                                size={'medium'}
                            >{`Fill Form`}</Button>
                        )}
                    </div>

                    <ProductRemovalModal
                        show={showProductRemovalModal}
                        setShow={(value) => {
                            if (!value) {
                                setProductRemoval(null);
                            }
                        }}
                        item={productRemoval}
                    />
                </>
            )}
        </div>
    );
}
